import { monotonicNow } from './monotonic-now.js';

export const HTTP_TOO_MANY_REQUESTS = 429;
const SERVER_RESEND_COOLDOWN_CODE = 'phone_resend_cooldown';
const SERVER_BUDGET_EXHAUSTED_CODE = 'phone_budget_exhausted';

// The session reserved one number, and the send has to name that same one. An
// empty session number never matches, so a send before a session opens is
// refused rather than sent to whatever the field holds.
export function matchesSessionPhone(sessionPhone, currentPhone) {
    return sessionPhone !== '' && sessionPhone === currentPhone;
}

// The channels to offer, in the order the server named them. Names the page has
// no button for are dropped rather than drawn blank, and a repeat is ignored so
// one channel can never hold two roles. The first survivor is the action to
// take, and the rest stay visible below it as alternatives.
export function offeredChannels(channels, supported) {
    if (!Array.isArray(channels)) return [];

    return channels.filter(function (channel, index) {
        return typeof channel === 'string'
            && supported.indexOf(channel) !== -1
            && channels.indexOf(channel) === index;
    });
}

// Whether the code step still owes the caller an SMS route. Same four terms as
// the mobile client's UsePhoneVerification.hasSmsAlternative, so both clients
// follow one rule. `sessionChannels` is the session's own list, never the
// narrower one an offer renders.
export function hasSmsAlternative({ route, sessionChannels, sentChannel, verifierRetryable }) {
    return route === 'whatsapp'
        && Array.isArray(sessionChannels)
        && sessionChannels.indexOf('sms') !== -1
        && sentChannel !== 'sms'
        && verifierRetryable === true;
}

export function setControlsDisabled(controls, disabled) {
    controls.filter(Boolean).forEach(function (control) {
        control.disabled = disabled;
    });
}

// Bounded diagnostic reasons for failures that prevent verification. The
// vocabulary is a contract with PhoneVerificationService::VERIFIER_FAILURE_REASONS
// and with the mobile client, so it never grows to fit a provider code.
export const VERIFIER_FAILURE_REASONS = {
    'auth/invalid-app-credential': 'invalid_app_credential',
    'auth/missing-app-credential': 'invalid_app_credential',
    'auth/captcha-check-failed': 'captcha_check_failed',
    'auth/network-request-failed': 'network',
    'auth/internal-error': 'internal_error',
    'auth/too-many-requests': 'throttled',
    'auth/quota-exceeded': 'quota_exceeded',
    // The page raises this one itself. Same literal as BLOCKED in verify-phone.js.
    'blocked': 'blocked',
    'unconfigured': 'unconfigured',
    'client_timeout': 'client_timeout',
};

// Keep send failures distinct from errors raised while confirming a delivered code.
export const SEND_REFUSAL_CODES = [
    'auth/internal-error',
    'auth/too-many-requests',
    'auth/quota-exceeded',
];

// The server stores this in a VARCHAR(512) and truncates to the same
// length, so a longer string only wastes the request.
export const VERIFIER_FAILURE_MESSAGE_MAX = 512;

// Firebase error code to the message key the error box shows. Codes absent
// here fall back to the generic message.
export const FIREBASE_MESSAGE_KEYS = {
    'auth/invalid-phone-number': 'number',
    'auth/missing-phone-number': 'number',
    'auth/invalid-app-credential': 'security',
    'auth/missing-app-credential': 'security',
    'auth/captcha-check-failed': 'security',
    'auth/invalid-verification-code': 'code',
    'auth/missing-verification-code': 'code',
    'auth/code-expired': 'code_expired',
    'auth/network-request-failed': 'network',
    // Firebase accepted the request and refused to send to this number.
    // Retrying the same number usually fails the same way, so the message
    // offers a different one rather than a plain retry.
    'auth/internal-error': 'undeliverable',
};

const FIREBASE_CODE_PREFIX = 'auth/';

// Every Firebase code the page recognises by name. Derived rather than listed,
// so a code named in either map above can never also count as unrecognised.
const RECOGNISED_FIREBASE_CODES = Object.keys(FIREBASE_MESSAGE_KEYS)
    .concat(Object.keys(VERIFIER_FAILURE_REASONS));

/**
 * Build the body of a verifier-failure report, or return null to send none.
 *
 * `sending` separates a refused send from an error raised while confirming a
 * code the provider already delivered.
 */
export function verifierFailureReport(error, sending) {
    const code = error && error.code;
    if (typeof code !== 'string' || code === '') return null;

    const mapped = VERIFIER_FAILURE_REASONS[code];
    // firebase-auth names a server error it does not recognise by slugifying
    // the server's own message, so a refused send arrives as auth/error-code:-39
    // rather than auth/internal-error. A code the page cannot name either is
    // therefore a refusal, and reports as internal_error, because the alternative
    // is no report and no report is also no waiver. The raw code travels in the
    // message instead of widening the reason vocabulary.
    //
    // Recognised codes are excluded, not merely unmapped: auth/invalid-phone-number
    // names the number the user typed, and waiving a gate for it would hand every
    // user a way past the gate.
    const reason = mapped
        || (sending && isUnrecognisedFirebaseCode(code) ? 'internal_error' : '');
    if (!reason) return null;
    if (!sending && SEND_REFUSAL_CODES.indexOf(code) !== -1) return null;

    const values = { reason: reason };
    const message = verifierFailureMessage(error, code, Boolean(mapped));
    if (message) values.message = message;

    return values;
}

function isUnrecognisedFirebaseCode(code) {
    return code.indexOf(FIREBASE_CODE_PREFIX) === 0
        && RECOGNISED_FIREBASE_CODES.indexOf(code) === -1;
}

function verifierFailureMessage(error, code, mapped) {
    const provider = error && error.message;
    const text = typeof provider === 'string' && provider !== code ? provider : '';
    // An unmapped code is the only record of which refusal this was, so it leads
    // the detail. A mapped code already reaches the server as the reason.
    const detail = mapped ? text : [code, text].filter(Boolean).join(' ');

    return detail.slice(0, VERIFIER_FAILURE_MESSAGE_MAX);
}

export function apiFailure(response, data, fallback, defaultRetryAfter) {
    const error = data && data.error;
    const isCooldown = response.status === HTTP_TOO_MANY_REQUESTS
        && error && error.code === SERVER_RESEND_COOLDOWN_CODE;
    const retryAfter = error && error.extra && Number(error.extra.retry_after);

    const isBudgetExhausted = response.status === HTTP_TOO_MANY_REQUESTS
        && error && error.code === SERVER_BUDGET_EXHAUSTED_CODE;

    return {
        code: response.status === HTTP_TOO_MANY_REQUESTS
            ? (isCooldown ? 'resend_cooldown' : (isBudgetExhausted ? 'budget_exhausted' : 'rate_limited'))
            : fallback,
        retryAfter: retryAfter > 0 ? retryAfter : (isCooldown ? defaultRetryAfter : null),
    };
}

// How often a deadline with a hold asks whether the user still holds it.
export const DEADLINE_HOLD_POLL_MS = 1000;
// A hold ends here even if it stays up, so a probe that misreads the page
// cannot keep a spinner up for the whole session.
export const DEADLINE_HOLD_MAX_MS = 120000;

/**
 * `after` delays the deadline until that promise settles, bounded by
 * `afterTimeoutMs`. A timeout result names the stage that ran out: 'after',
 * 'deadline', 'released' or 'hold'. While `hold` returns true the deadline
 * stops, and it restarts in full once the hold ends. A deadline that ran out
 * after a hold is 'released'. All holds together end at the cap.
 */
export function withDeadline(promise, timeoutMs, timers = globalThis, {
    after = null,
    afterTimeoutMs = 0,
    hold = null,
} = {}) {
    return new Promise((resolve) => {
        let settled = false;
        let armed = false;
        let held = false;
        let wasHeld = false;
        let heldMs = 0;
        let timer = null;
        let afterTimer = null;
        let pollTimer = null;
        const clear = (id) => {
            if (id !== null) timers.clearTimeout(id);
            return null;
        };
        const finish = (result) => {
            if (settled) return;
            settled = true;
            timer = clear(timer);
            afterTimer = clear(afterTimer);
            pollTimer = clear(pollTimer);
            resolve(result);
        };
        const holding = () => Boolean(hold && hold());
        const startDeadline = () => {
            timer = clear(timer);
            timer = timers.setTimeout(() => {
                timer = null;
                if (holding()) {
                    held = true;
                    wasHeld = true;
                    return;
                }
                finish({ timedOut: true, stage: wasHeld ? 'released' : 'deadline' });
            }, timeoutMs);
        };
        const poll = () => {
            pollTimer = null;
            if (settled) return;
            if (holding()) {
                timer = clear(timer);
                held = true;
                wasHeld = true;
                heldMs += DEADLINE_HOLD_POLL_MS;
                if (heldMs >= DEADLINE_HOLD_MAX_MS) {
                    finish({ timedOut: true, stage: 'hold' });
                    return;
                }
            } else if (held) {
                held = false;
                startDeadline();
            }
            pollTimer = timers.setTimeout(poll, DEADLINE_HOLD_POLL_MS);
        };
        const arm = () => {
            if (settled || armed) return;
            armed = true;
            afterTimer = clear(afterTimer);
            if (holding()) {
                held = true;
                wasHeld = true;
            } else {
                startDeadline();
            }
            if (hold) pollTimer = timers.setTimeout(poll, DEADLINE_HOLD_POLL_MS);
        };

        if (after) {
            if (afterTimeoutMs > 0) {
                afterTimer = timers.setTimeout(() => {
                    afterTimer = null;
                    if (!armed) finish({ timedOut: true, stage: 'after' });
                }, afterTimeoutMs);
            }
            Promise.resolve(after).then(arm, arm);
        } else {
            arm();
        }

        Promise.resolve(promise).then(
            (value) => finish({ timedOut: false, value }),
            (error) => finish({ timedOut: false, error }),
        );
    });
}

export function createSendOwnership({ durationMs, now = monotonicNow }) {
    const expiresAt = now() + Math.max(0, durationMs);
    let active = true;
    let settled = false;
    let timedOut = false;

    return {
        expire() {
            active = false;
        },
        get pending() {
            return active && !settled && now() < expiresAt;
        },
        get remainingMs() {
            return Math.max(0, expiresAt - now());
        },
        get timedOut() {
            return timedOut;
        },
        markTimedOut() {
            if (this.pending) timedOut = true;
        },
        settle() {
            if (!this.pending) return { accepted: false, timedOut };
            settled = true;
            return { accepted: true, timedOut };
        },
    };
}

export function createSendRecoveryCoordinator({
    clearDeadline,
    now,
    timers,
    wallNow,
    writeDeadline,
}) {
    let current = null;

    function clear(ownership) {
        if (current !== ownership) return false;
        current = null;
        clearDeadline();
        return true;
    }

    return {
        clear() {
            if (current) current.expire();
            current = null;
            clearDeadline();
        },
        suspend() {
            if (current) current.expire();
            current = null;
        },
        get active() {
            return Boolean(current && current.pending);
        },
        own({
            deadline = {},
            durationMs,
            onExpire,
            onReject,
            onResolve,
            onTimeout,
            promise,
            retainTimedOutRejection = false,
            timeoutMs,
        }) {
            const ownership = createSendOwnership({ durationMs, now });
            current = ownership;
            writeDeadline(wallNow() + durationMs);
            const lifetimeTimer = timers.setTimeout(() => {
                ownership.expire();
                if (!clear(ownership)) return;
                onExpire();
            }, durationMs);

            withDeadline(promise, timeoutMs, timers, deadline).then((result) => {
                if (!result.timedOut || current !== ownership || !ownership.pending) return;
                ownership.markTimedOut();
                onTimeout(result.stage);
            });

            Promise.resolve(promise).then((value) => {
                const remainingMs = ownership.remainingMs;
                const settlement = ownership.settle();
                if (!settlement.accepted || !clear(ownership)) return;
                timers.clearTimeout(lifetimeTimer);
                onResolve(value, { remainingMs, timedOut: settlement.timedOut });
            }, (error) => {
                if (retainTimedOutRejection && ownership.pending && ownership.timedOut) {
                    return;
                }
                const settlement = ownership.settle();
                if (!settlement.accepted || !clear(ownership)) return;
                timers.clearTimeout(lifetimeTimer);
                onReject(error, { timedOut: settlement.timedOut });
            });

            return ownership;
        },
    };
}

// The client_timeout phase each withDeadline stage reports. reCAPTCHA has no
// dismiss event, so a closed challenge leaves verify() pending until 'released'.
const SEND_TIMEOUT_PHASES = {
    after: 'verifier',
    deadline: 'send',
    hold: 'challenge',
    released: 'challenge_closed',
};

export function startPhoneSendAttempt({
    coordinator,
    onExpire,
    onFailure,
    onProviderAccepted,
    onSessionAccepted,
    onTimeout,
    providerSend,
    // Resolves once the provider can send. The send deadline starts there, so
    // a cold verifier load answers to `readyTimeoutMs` instead.
    providerReady = null,
    readyTimeoutMs = 0,
    // True while the user holds the send, as with an open captcha challenge.
    holdDeadline = null,
    requestSession,
    sessionLifetimeMs,
    // Answers whether the accepted session may reach the provider at all. A
    // WhatsApp-route session is offered to the caller instead of sent on, and
    // returning false releases the ownership without a provider call.
    shouldSend = () => true,
    timeoutMs,
}) {
    const sessionPromise = requestSession();
    coordinator.own({
        durationMs: sessionLifetimeMs,
        onExpire,
        onReject: onFailure,
        onResolve(session, ownershipState) {
            if (!session) {
                onExpire();
                return;
            }
            const durationMs = Math.max(0, Math.min(
                ownershipState.remainingMs,
                Number(session.expires_in) * 1000,
            ));
            if (!Number.isFinite(durationMs) || durationMs <= 0) {
                onExpire();
                return;
            }

            onSessionAccepted(session, durationMs);
            if (shouldSend(session) === false) {
                onExpire();
                return;
            }

            const readiness = providerReady
                ? Promise.resolve().then(() => providerReady(session))
                : null;
            const providerPromise = Promise.resolve().then(() => providerSend(session));
            coordinator.own({
                deadline: {
                    after: readiness,
                    afterTimeoutMs: readyTimeoutMs,
                    hold: holdDeadline,
                },
                durationMs,
                onExpire,
                onReject: onFailure,
                onResolve: onProviderAccepted,
                onTimeout: (stage) => onTimeout(SEND_TIMEOUT_PHASES[stage] || 'send'),
                promise: providerPromise,
                retainTimedOutRejection: true,
                timeoutMs,
            });
        },
        onTimeout: () => onTimeout('session'),
        promise: sessionPromise,
        timeoutMs,
    });
}

export function createCooldownController({
    clearExpiry,
    clearInterval,
    defaultLabel,
    defaultSeconds,
    formatLabel,
    now,
    onRestore = () => {},
    onExpire = () => {},
    onTick = () => {},
    readExpiry,
    resendButton,
    resendLabel,
    sendButton,
    setInterval,
    writeExpiry,
}) {
    let expiresAt = 0;
    let remaining = 0;
    let timer = null;
    let resendBusy = false;
    let sendBusy = false;
    // What the control reads when no cooldown runs. A fallback send needs a new
    // session to try again, so the caller relabels it rather than leaving a
    // resend control that cannot resend.
    let idleLabel = defaultLabel;

    function render() {
        if (sendButton) sendButton.disabled = sendBusy || remaining > 0;
        if (resendButton) resendButton.disabled = resendBusy || remaining > 0;
        if (resendLabel) {
            resendLabel.textContent = remaining > 0 ? formatLabel(remaining) : idleLabel;
        }
        onTick(remaining);
    }

    function clearTimer() {
        if (timer) clearInterval(timer);
        timer = null;
    }

    function stop() {
        clearTimer();
        expiresAt = 0;
        remaining = 0;
        clearExpiry();
        render();
        onExpire();
    }

    function tick() {
        remaining = Math.max(0, Math.ceil((expiresAt - now()) / 1000));
        if (remaining <= 0) {
            stop();
            return;
        }
        render();
    }

    return {
        get remaining() {
            return remaining;
        },
        setResendBusy(busy) {
            resendBusy = busy;
            render();
        },
        setSendBusy(busy) {
            sendBusy = busy;
            render();
        },
        setIdleLabel(label) {
            idleLabel = label || defaultLabel;
            render();
        },
        start(seconds) {
            clearTimer();
            const duration = seconds > 0 ? seconds : defaultSeconds;
            expiresAt = now() + (duration * 1000);
            remaining = duration;
            writeExpiry(expiresAt);
            render();
            timer = setInterval(tick, 1000);
        },
        restore() {
            clearTimer();
            expiresAt = Number(readExpiry()) || 0;
            remaining = Math.max(0, Math.ceil((expiresAt - now()) / 1000));
            if (remaining <= 0) {
                stop();
                return;
            }
            render();
            onRestore(remaining);
            timer = setInterval(tick, 1000);
        },
        stop,
    };
}

export async function confirmWithCachedToken({
    code,
    postConfirmation,
    responseJson,
    state,
    warn = () => {},
}) {
    const sessionGeneration = state.sessionGeneration;
    const nonce = state.nonce;
    const confirmationResult = state.confirmationResult;

    function isCurrentSession() {
        return state.sessionGeneration === sessionGeneration;
    }

    if (!state.firebaseIdToken) {
        if (!state.firebaseUser) {
            const credential = await confirmationResult.confirm(code);
            if (!isCurrentSession()) return { stale: true };
            state.firebaseUser = credential.user;
        }

        const firebaseUser = state.firebaseUser;
        const firebaseIdToken = await firebaseUser.getIdToken();
        if (!isCurrentSession()) return { stale: true };
        state.firebaseIdToken = firebaseIdToken;
        try {
            if (state.auth) {
                await state.auth.signOut();
            }
        } catch (error) {
            warn(error);
        }
        state.firebaseUser = null;
    }

    const firebaseIdToken = state.firebaseIdToken;
    const response = await postConfirmation(firebaseIdToken, nonce);
    const data = await responseJson(response);
    if (!isCurrentSession()) return { data, response, stale: true };
    const verified = response.ok && data.status === 'verified';
    const terminal = !verified && response.status !== HTTP_TOO_MANY_REQUESTS && response.status < 500;

    if (verified || terminal) {
        state.firebaseIdToken = '';
        state.firebaseUser = null;
        state.confirmationResult = null;
    }

    return { data, response, stale: false, terminal, verified };
}
